import { useState, useMemo } from 'react';
import { useProductPlans } from '@/hooks/useProductPlans';
import { useProducts } from '@/hooks/useProducts';
import { Button } from '@/components/ui/button';
import { Plus, ArrowUpDown, ArrowUp, ArrowDown, Pencil, CreditCard } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ProductPlanForm } from './ProductPlanForm';
import type { ProductPlanWithProduct } from '@/types/productPlans';

export function ProductPlansList() {
  const { plans, isLoading } = useProductPlans();
  const { products } = useProducts();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<ProductPlanWithProduct | null>(null);
  
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  const sortedPlans = useMemo(() => {
    if (!sortConfig || !plans) return plans;

    return [...plans].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.key) {
        case 'product_name':
          aValue = a.product_name;
          bValue = b.product_name;
          break;
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'recurrence':
          aValue = a.recurrence_type;
          bValue = b.recurrence_type;
          break;
        case 'price':
          aValue = a.price;
          bValue = b.price;
          break;
        case 'currency':
          aValue = a.currency;
          bValue = b.currency;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [plans, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig((current) => {
      if (!current || current.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return null;
    });
  };

  const getSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-4 w-4 ml-1" />
    ) : (
      <ArrowDown className="h-4 w-4 ml-1" />
    );
  };

  const getRecurrenceLabel = (type: string) => {
    const labels = {
      daily: 'Diário',
      weekly: 'Semanal',
      monthly: 'Mensal',
      bimonthly: 'Bimestral',
      quarterly: 'Trimestral',
      semiannual: 'Semestral',
      annual: 'Anual',
      lifetime: 'Vitalício',
      custom: 'Customizada',
    };
    return labels[type as keyof typeof labels] || type;
  };

  const formatPrice = (price: number, currency: string) => {
    const symbols = { BRL: 'R$', USD: '$', EUR: '€' };
    return `${symbols[currency as keyof typeof symbols]} ${price.toFixed(2).replace('.', ',')}`;
  };

  const handleEdit = (plan: ProductPlanWithProduct) => {
    setEditingPlan(plan);
    setIsFormOpen(true);
  };

  const handleCreate = () => {
    setEditingPlan(null);
    setIsFormOpen(true);
  };

  if (isLoading) {
    return <div className="flex justify-center p-8">Carregando...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {plans?.length || 0} plano(s) cadastrado(s)
        </p>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Plano
        </Button>
      </div>

      <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Logo</TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-accent/50"
                onClick={() => handleSort('product_name')}
              >
                <div className="flex items-center">
                  Produto
                  {getSortIcon('product_name')}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-accent/50"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center">
                  Nome do Plano
                  {getSortIcon('name')}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-accent/50"
                onClick={() => handleSort('recurrence')}
              >
                <div className="flex items-center">
                  Recorrência
                  {getSortIcon('recurrence')}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-accent/50"
                onClick={() => handleSort('currency')}
              >
                <div className="flex items-center">
                  Moeda
                  {getSortIcon('currency')}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-accent/50 text-right"
                onClick={() => handleSort('price')}
              >
                <div className="flex items-center justify-end">
                  Valor
                  {getSortIcon('price')}
                </div>
              </TableHead>
              <TableHead>ID Gateway</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedPlans?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8">
                  Nenhum plano cadastrado ainda
                </TableCell>
              </TableRow>
            ) : (
              sortedPlans?.map((plan) => (
                <TableRow key={plan.id}>
                  <TableCell>
                    <Avatar className="h-8 w-8">
                      {plan.product_logo_url ? (
                        <AvatarImage src={plan.product_logo_url} alt={plan.product_name} />
                      ) : (
                        <AvatarFallback className="bg-primary/10">
                          <CreditCard className="h-4 w-4 text-primary" />
                        </AvatarFallback>
                      )}
                    </Avatar>
                  </TableCell>
                  <TableCell className="font-medium">{plan.product_name}</TableCell>
                  <TableCell>{plan.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {getRecurrenceLabel(plan.recurrence_type)}
                    </Badge>
                  </TableCell>
                  <TableCell>{plan.currency}</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatPrice(plan.price, plan.currency)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {plan.gateway_plan_id || '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={plan.is_active ? 'default' : 'secondary'}>
                      {plan.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(plan)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

      <ProductPlanForm
        open={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingPlan(null);
        }}
        plan={editingPlan}
        products={products || []}
      />
    </div>
  );
}
